import React, {useEffect, useState} from 'react';
import {Alert, Box, TextField, Typography, MenuItem, Checkbox, FormControlLabel} from '@mui/material';
import AdmicityGroupFilterSelect from '../../../../shared-components/AdmicityGroupFilterSelect';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import useTextField from '../../../../utility/hooks/useTextField';
import {validateFieldLength} from '../../../../utility/validationUtil';
import {useGetFormCategoriesQuery} from '../../../../api/services/formCategoriesService';
import PropTypes from 'prop-types';
import {availableComponents} from '../FormBuilder/FormBuilder';
import {determinePossibleOperations} from '../FormBuilder/ShowFieldWhen';
import AdmicityTabs from '../../../../shared-components/AdmicityTabs';

const CreateGroupFromResponsesDialog = ({
                                            open,
                                            title,
                                            onClose,
                                            onCreateGroup,
                                            onExtendGroup,
                                            schema,
                                            lookups,
                                            groupFilters,
                                            groups,
                                            membersType
                                        }) => {
    const nameField = useTextField({
        initialValue: '',
        validate: (value) => validateFieldLength(value, 256),
    });
    const {data: formCategories} = useGetFormCategoriesQuery();
    const [parentGroupFilterId, setParentGroupFilterId] = useState('');
    const [existingGroupFilterId, setExistingGroupFilterId] = useState('');
    const [selectedGroupLevel, setSelectedGroupLevel] = useState(null);
    const [parentGroupLevel, setParentGroupLevel] = useState(null);
    const [isGroupDynamic, setIsGroupDynamic] = useState(true);
    const [myGroup, setMyGroup] = useState(false);
    const [formCategoryId, setFormCategoryId] = useState(null);
    const [relatedComponent, setRelatedComponent] = useState(null);
    const [targetState, setTargetState] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const filteredGroupFilters = groupFilters?.modules
        .map(module => ({
            ...module,
            filters: module.filters.filter(filter => filter.membersType === membersType)
        })) || [];
    const groupFilterIds = groups?.map(group => group.groupFilterId) || [];

    const findGroupLevel = (groupId) => {
        if (!groupFilters?.modules) return null;

        for (const module of groupFilters.modules) {
            for (const filter of module.filters) {
                if (filter.id === groupId) {
                    return filter.level;
                }
            }
        }
        return null;
    };

    const resetState = () => {
        nameField.reset();
        setParentGroupFilterId('');
        setExistingGroupFilterId('');
        setSelectedGroupLevel(null);
        setParentGroupLevel(null);
        setMyGroup(false);
        setFormCategoryId(null);
        setIsGroupDynamic(true);
        setRelatedComponent(null);
        setTargetState(null);
    };

    useEffect(() => {
        if (!open) {
            resetState();
            setActiveTab(0);
        }
    }, [open]);

    const handleCategoryChange = (e) => {
        const selectedCategoryId = e.target.value || null;
        setFormCategoryId(selectedCategoryId);
    };

    const handleSave = async () => {
        if (activeTab === 0) {
            await onCreateGroup({
                parentGroupFilterId,
                groupName: nameField.value,
                myGroup,
                formCategoryId,
                isDynamic: isGroupDynamic,
                relatedComponentId: relatedComponent.lookupType ? `${relatedComponent.id}.id` : relatedComponent.id,
                targetStateId: targetState
            });
        } else {
            await onExtendGroup({
                existingGroupFilterId,
                relatedComponentId: relatedComponent.lookupType ? `${relatedComponent.id}.id` : relatedComponent.id,
                targetStateId: targetState
            });
        }
    };

    const renderQuestionSelectComponents = () => (
        <>
            <TextField
                select
                value={relatedComponent || ''}
                disabled={!schema}
                label={'Question'}
                required
                onChange={e => setRelatedComponent(e.target.value)}
            >
                {schema?.filter(option => availableComponents.includes(option.type)).length > 0
                    ? (
                        schema
                            .filter(option => availableComponents.includes(option.type))
                            .map(option => (
                                <MenuItem key={option.id} value={option} sx={{maxWidth: '550px'}}>
                                    <div
                                        title={`${option.label} - ${option.description}`}
                                        style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                                    >
                                        {option.label} - {option.description}
                                    </div>
                                </MenuItem>
                            ))
                    )
                    : (
                        <MenuItem disabled>No options available</MenuItem>
                    )}
            </TextField>
            <TextField
                select
                value={targetState}
                label={'Equals'}
                required
                disabled={!relatedComponent}
                onChange={e => setTargetState(e.target.value)}
            >
                {relatedComponent &&
                    determinePossibleOperations(relatedComponent.lookupType
                        ? {...relatedComponent, options: lookups[relatedComponent.lookupType] || []}
                        : relatedComponent, { checkboxOnlyChecked: true })
                        ?.map(operation =>
                            <MenuItem key={operation.value} value={operation.value} sx={{maxWidth: '550px'}}>
                                <div title={operation.title} style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                    {operation.title}
                                </div>
                            </MenuItem>)}
            </TextField>
        </>
    );

    const tabs = [
        {
            label: 'Export to New Group',
            component: (
                <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
                    <TextField
                        required
                        fullWidth
                        label="Group Name"
                        variant="outlined"
                        {...nameField}
                    />
                    <TextField
                        select
                        value={formCategoryId || ''}
                        label="Category"
                        helperText="Please select a category or clear the selection"
                        fullWidth
                        onChange={handleCategoryChange}
                        SelectProps={{
                            renderValue: (selected) => {
                                if (!selected) return 'No category selected';
                                const selectedCategory = formCategories.find(
                                    (category) => category.id === selected
                                );
                                if (!selectedCategory) return 'Invalid category selected';
                                return (
                                    <Box display="flex" alignItems="center">
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: selectedCategory.color || 'grey',
                                                marginRight: 1,
                                            }}
                                        />
                                        {selectedCategory.name || ''}
                                    </Box>
                                );
                            },
                        }}
                    >
                        <MenuItem value="">
                            <Box display="flex" alignItems="center" sx={{color: 'grey'}}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: 'transparent',
                                        marginRight: 1,
                                    }}
                                />
                                No category
                            </Box>
                        </MenuItem>
                        {formCategories?.length > 0 &&
                            formCategories.map(({id, name, color}) => (
                                <MenuItem key={id} value={id}>
                                    <Box display="flex" alignItems="center">
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: color || 'grey',
                                                marginRight: 1,
                                            }}
                                        />
                                        {name}
                                    </Box>
                                </MenuItem>
                            ))}
                    </TextField>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Parent Group
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            Choose if you want to add this as a sub-group
                        </Typography>
                        <AdmicityGroupFilterSelect
                            sx={{marginTop: 1, width: '100%'}}
                            data={filteredGroupFilters}
                            value={parentGroupFilterId}
                            onChange={(nodeId, paths) => {
                                setParentGroupFilterId(nodeId, paths);
                                setParentGroupLevel(findGroupLevel(nodeId));
                            }}
                            isRequired={false}
                        />
                        {parentGroupLevel && parentGroupLevel > 2 && (
                            <Alert severity="warning" sx={{mt: 1}}>
                                Only profiles that exist in the parent group will be exported to this subgroup.
                            </Alert>
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isGroupDynamic}
                                    onChange={(e) => setIsGroupDynamic(e.target.checked)}/>
                            }
                            label="Dynamic Group (auto-includes students)"
                        />
                    </Box>
                    {renderQuestionSelectComponents()}
                </Box>
            ),
            isValid: () => nameField.value &&
                !nameField.error &&
                relatedComponent &&
                targetState !== null
        },
        {
            label: 'Export to Existing Group',
            component: (
                <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
                    <AdmicityGroupFilterSelect
                        sx={{width: '100%'}}
                        data={groupFilters?.modules
                            .map(module => ({
                                ...module,
                                filters: module.filters.filter(filter => filter.membersType === membersType)
                            }))
                        }
                        value={existingGroupFilterId}
                        onChange={(nodeId) => {
                            setExistingGroupFilterId(nodeId);
                            setSelectedGroupLevel(findGroupLevel(nodeId));
                        }}
                        isRequired={false}
                        label={'Existing group'}
                    />
                    {groupFilterIds.includes(existingGroupFilterId) && (
                        <Alert severity="error" sx={{mt: 1}}>
                            This group is already linked to this form. Please select a different group.
                        </Alert>
                    )}
                    {selectedGroupLevel && selectedGroupLevel > 2 && (
                        <Alert severity="warning" sx={{mt: 1}}>
                            Only profiles that exist in the parent group will be exported to this subgroup.
                        </Alert>
                    )}
                    {renderQuestionSelectComponents()}
                </Box>
            ),
            isValid: () => {
                const isGroupAlreadyLinked = groupFilterIds.includes(existingGroupFilterId);
                return relatedComponent && targetState !== null && !isGroupAlreadyLinked;
            }
        }
    ];

    return (
        <AdmicityDialog
            handleClose={onClose}
            title={title}
            open={open}
            actions={[
                {
                    label: 'Cancel',
                    onClick: onClose,
                },
                {
                    label: 'Save',
                    disabled: !tabs[activeTab]?.isValid(),
                    onClick: handleSave,
                },
            ]}
        >
            <Alert severity="info">
                Please note that students will be assigned to the new group based on the filters applied to the form
                responses
            </Alert>
            <AdmicityTabs
                tabs={tabs}
                variant="fullWidth"
                tabIndex={activeTab}
                onChange={(tabIndex) => {
                    resetState();
                    setActiveTab(tabIndex);
                }}
                slots={{
                    panel: {
                        paddingRight: 0,
                        paddingLeft: 0
                    }
                }}
            />
        </AdmicityDialog>
    );
};

CreateGroupFromResponsesDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.node.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreateGroup: PropTypes.func.isRequired,
    onExtendGroup: PropTypes.func.isRequired,
    schema: PropTypes.object.isRequired,
    lookups: PropTypes.object,
    groupFilters: PropTypes.object,
    groups: PropTypes.array,
    membersType: PropTypes.number
};

export default CreateGroupFromResponsesDialog;
